import React, {FC, ReactNode, useCallback, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useTimeout} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {getHasConversationSynopsis} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {tooltipDefaultDelay} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipHelpers';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {toggleSynopsisAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {SynopsisFiltersEnum} from '../../../../../store/panels/viewer/viewerPanelState';
import {HoverLinkedConversationsPreviewerUnfurlContainer} from '../../activity/linkedConversation/hoverLinkedConversationPreviewerUnfurlContainer';
import {ConversationSummaryContainer} from '../conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../conversationSummary/conversationSummarySections';
import {StyledPopoverCoordinatorWrapperDiv} from './conversationHeaderPill';
import {useJustPinnedAnimation} from './conversationPinnedActivitiesButton';

/*
 * Props and Types.
 */

interface ConversationLinkedConversationsButtonProps {
  panelId: ViewerPanelId;
  conversation: ConversationModel;
  teammateId: number;
  className?: string;
  conversationSummarySection: ConversationSummarySectionsEnum;
  shouldDisableCount?: boolean;
}

enum AnimationStatesEnum {
  NORMAL = 'NORMAL',
  JUST_PINNED = 'JUST_PINNED',
  JUST_PINNED_OUTRO = 'JUST_PINNED_OUTRO',
}

/*
 * Styles.
 */

// Animation length constants
const fadeAnimationDurationMs = 250;

interface LinkedButtonStyleProps {
  $animationState: AnimationStatesEnum;

  $backgroundColor: string;
  $borderColor: string;

  $hoverBackgroundColor: string;
  $hoverBorderColor: string;
}

const StyledLinkedButton = styled(ChromelessButton)<LinkedButtonStyleProps>`
  background-color: ${(p) => p.$backgroundColor};
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1.5px 8px;
  height: 20px;
  width: max-content;

  margin-left: 1px;
  border: ${(p) => p.$borderColor} 1px solid;

  ${(p) => maybeAddTransition(p)}

  &:hover {
    transition: none;
    background-color: ${(p) => p.$hoverBackgroundColor};
    border-color: ${(p) => p.$hoverBorderColor};
  }
`;

function maybeAddTransition(props: LinkedButtonStyleProps) {
  if (props.$animationState === AnimationStatesEnum.NORMAL) {
    return null;
  }

  return css`
    transition:
      background-color ${fadeAnimationDurationMs}ms ease-out,
      border-color ${fadeAnimationDurationMs}ms ease-out;
  `;
}

interface PinnedButtonBodyStyleProps {
  $color: string;
}
const PinnedButtonBodySpan = styled.span<PinnedButtonBodyStyleProps>`
  color: ${(p) => p.$color};
  font-size: 12px;
  font-weight: ${fontWeights.semibold};
  transition: color ${fadeAnimationDurationMs}ms ease-out;
`;

const StyledAnimatedIcon = styled(Icon)`
  transition: color ${fadeAnimationDurationMs}ms ease-out;
`;

const makeNormalColors = (theme: UserTheme) => ({
  backgroundColor: theme.palette.grey.shade10,
  hoverBackgroundColor: theme.palette.grey.shade20,
  hoverBorderColor: theme.palette.grey.shade50,
  borderColor: theme.palette.grey.shade40,
  textColor: theme.palette.grey.shade40,
  iconColor: theme.palette.grey.shade40,
});

const makeJustLinkedColors = (theme: UserTheme) => ({
  backgroundColor: theme.palette.blue.shade40,
  hoverBackgroundColor: theme.palette.blue.shade50,
  hoverBorderColor: theme.palette.blue.shade50,
  borderColor: theme.palette.blue.shade40,
  textColor: theme.greys.white,
  iconColor: theme.greys.white,
});

/*
 * Component.
 */

export const ConversationLinkedConversationsButton: FC<ConversationLinkedConversationsButtonProps> = (
  props,
) => {
  const [isConversationSummaryOpen, setIsConversationSummaryOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const {trackDataEvent} = useDataEventTracking();

  const hasConversationSynopsisFF = useAppSelector(getHasConversationSynopsis);
  const dispatch = useAppDispatch();

  const {numLinkedConversations} = props.conversation;
  const [animationState, setAnimationState] = useJustPinnedAnimation(
    props.conversation.id,
    numLinkedConversations,
  );
  const theme = useUserTheme();
  const wasJustLinked = animationState === AnimationStatesEnum.JUST_PINNED; // Convenience helper.

  const regularColors = makeNormalColors(theme);
  const justLinkedColors = makeJustLinkedColors(theme);

  // Immediately disable our animation states so we stop showing the blue icon when the user clicks it.
  const onClick = useCallback(() => {
    setShouldShowPreview(false);
    setAnimationState(AnimationStatesEnum.NORMAL);

    if (hasConversationSynopsisFF) {
      dispatch(toggleSynopsisAction(props.panelId, true, SynopsisFiltersEnum.LINKED_CONVERSATIONS));
    } else {
      setIsConversationSummaryOpen(true);
    }

    trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.P3_PILL);
  }, [dispatch, hasConversationSynopsisFF, props.panelId, setAnimationState, trackDataEvent]);

  const [safeSetTimeout] = useTimeout();

  const onMouseEnter = () => {
    safeSetTimeout(() => {
      setShouldShowPreview(true);
    }, tooltipDefaultDelay);
  };

  const onMouseLeave = () => {
    safeSetTimeout(() => {
      setShouldShowPreview(false);
    }, tooltipDefaultDelay);
  };

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen ? (
      <ConversationSummaryContainer
        conversation={props.conversation}
        teammateId={props.teammateId}
        onRequestClose={() => setIsConversationSummaryOpen(false)}
        initiallyExpandedSection={props.conversationSummarySection}
        panelId={props.panelId}
      />
    ) : null;

  const linkedButtonProps = {
    ...(wasJustLinked ? justLinkedColors : regularColors),
    animationState,
    ...buildLinkedButtonProps(props.conversationSummarySection, props.conversation),
  };

  // See comment at maybeRenderConversationPinnedActivitiesButton in conversationPinnedActivitiesButton.tsx
  const maybeRenderConversationLinkedConversationsButton = () =>
    numLinkedConversations ? (
      <StyledPopoverCoordinatorWrapperDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <StyledLinkedButton
          $animationState={linkedButtonProps.animationState}
          $backgroundColor={linkedButtonProps.backgroundColor}
          $borderColor={linkedButtonProps.borderColor}
          $hoverBackgroundColor={linkedButtonProps.hoverBackgroundColor}
          $hoverBorderColor={linkedButtonProps.hoverBorderColor}
          onClick={onClick}
          interactionId={linkedButtonProps.interactionId}
          ref={anchor}
        >
          <StyledAnimatedIcon
            name={linkedButtonProps.iconName}
            size={linkedButtonProps.iconSize}
            color={wasJustLinked ? justLinkedColors.iconColor : regularColors.iconColor}
          />

          <PinnedButtonBodySpan $color={wasJustLinked ? justLinkedColors.textColor : regularColors.textColor}>
            {!props.shouldDisableCount && `${numLinkedConversations} `}
            {linkedButtonProps.buttonText && <>{linkedButtonProps.buttonText}</>}
          </PinnedButtonBodySpan>
        </StyledLinkedButton>
        <HoverLinkedConversationsPreviewerUnfurlContainer
          shouldShowPreview={shouldShowPreview}
          anchor={anchor}
          panelId={props.panelId}
          onRequestClose={onMouseLeave}
          linkStatus={props.conversation.linkStatus}
          conversation={props.conversation}
        />
      </StyledPopoverCoordinatorWrapperDiv>
    ) : null;

  // NOTE: The button can exist without the Conversation Summary modal and vice versa.
  return (
    <>
      {maybeRenderConversationLinkedConversationsButton()}
      {maybeRenderConversationSummary()}
    </>
  );
};

/*
 * Helpers.
 */

interface LinkedButtonProps {
  iconName: IconName;
  iconSize?: number;
  interactionId?: string;
  buttonText?: ReactNode;
}

function buildLinkedButtonProps(
  conversationSummarySection: ConversationSummarySectionsEnum,
  conversation: ConversationModel,
): LinkedButtonProps {
  switch (conversationSummarySection) {
    case ConversationSummarySectionsEnum.LINKED_CONVERSATIONS: {
      const isOriginalConversation = conversation.linkStatus === LinkedConversationStatusesEnum.ORIGINAL;
      const buttonText = isOriginalConversation ? (
        <Trans id="linked-conversations-original-buttondesc-">linked</Trans>
      ) : (
        <Trans id="linked-conversations-linked-buttondesc">Linked</Trans>
      );

      return {
        iconName: 'linkedConversationsFilledMedium',
        interactionId: 'conversationLinkedConversationsButton',
        buttonText,
      };
    }
    default:
      throw new Error('This should not happen');
  }
}
